// Helpers de ciclo de vida de la cuenta: deletion y limpieza local al salir.

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { supabase } from './supabase';

// sb-* cubre el JWT cacheado de Supabase. En el flujo de deletion, el usuario
// ya no existe cuando se intenta signOut() — el SDK puede dejar la clave
// huérfana, así que la barremos explícitamente.
const LOCALSTORAGE_PREFIXES = ['mipae:', 'mipae_', 'mipae.', 'sb-'];

// Preferencias del DISPOSITIVO, no del usuario: cómo se ve y se comporta este
// navegador, sin nada de la persona ni de sus pacientes. Sobreviven al logout
// (2026-08-16) para no dejarle el tema en claro a quien cierra sesión en su
// propio teléfono; el borrado de cuenta sigue barriendo todo.
// Regla para ampliar esta lista: solo entra una clave que no diga NADA de
// quién usó la app. Ante la duda, se borra.
// mipae_tema (2026-09-08): tema de ambiente elegido, un ajuste visual sin
// nada de la persona.
// mipae_texto (2026-09-08): tamaño del texto elegido, mismo criterio.
export const PREFERENCIAS_DISPOSITIVO = ['mipae_theme', 'mipae_install_dismissed', 'mipae_tema', 'mipae_texto', 'mipae_letra'];

// En web: '' (relativo). En Capacitor: REACT_APP_API_URL=https://mipae.vercel.app
const API_BASE = process.env.REACT_APP_API_URL || '';

/**
 * Llama al endpoint server-side de borrado de cuenta. Requiere sesión activa.
 * Tras éxito, el caller debe hacer signOut + clearLocalMipaeKeys.
 *
 * @param {string} emailConfirmation — email tipeado por el usuario en el modal
 * @returns {Promise<{status: 'deleted'}>}
 * @throws Error con .code en { 'no_session','email_mismatch','unauthorized','deletion_failed','network_error' }
 */
export async function deleteAccount(emailConfirmation) {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) {
    const err = new Error('no_session');
    err.code = 'no_session';
    throw err;
  }

  let resp;
  try {
    resp = await fetch(`${API_BASE}/api/delete-account`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email_confirmation: emailConfirmation,
        app_version: process.env.REACT_APP_VERSION || null,
      }),
    });
  } catch (e) {
    const err = new Error('network_error');
    err.code = 'network_error';
    throw err;
  }

  if (!resp.ok) {
    let code = 'deletion_failed';
    try {
      const body = await resp.json();
      if (body?.error) code = body.error;
    } catch { /* ignore */ }
    const err = new Error(code);
    err.code = code;
    throw err;
  }
  return resp.json();
}

/**
 * Limpia todas las claves de localStorage propias de MiPAE.
 * Usado tras borrado de cuenta y tras logout para evitar fuga de borradores.
 *
 * El caso real que lo hizo obligatorio en el logout (usuaria del piloto,
 * 2026-08-16): dejó la sesión abierta en un computador prestado y se fue.
 * Cerrar sesión a distancia mataba la sesión en el servidor, pero el borrador
 * del turno (camas, notas, bitácora) seguía en ESE navegador hasta expirar por
 * tiempo. En un computador de estación de enfermería eso es el hueco de verdad.
 *
 * @param {Object} [opts]
 * @param {string[]} [opts.conservar=[]] — claves exactas que NO se borran.
 *   Pensado para `PREFERENCIAS_DISPOSITIVO` en el logout; el borrado de cuenta
 *   llama sin argumentos y barre todo.
 */
export function clearLocalMipaeKeys({ conservar = [] } = {}) {
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && !conservar.includes(k) && LOCALSTORAGE_PREFIXES.some(p => k.startsWith(p))) {
        toRemove.push(k);
      }
    }
    toRemove.forEach(k => localStorage.removeItem(k));
  } catch {
    // entornos sin localStorage (SSR, modo incógnito restrictivo) — ignorar
  }
}

/**
 * En nativo la sesión vive en el almacenamiento del sistema (@capacitor/
 * preferences, ver `storageNativo` en lib/supabase.js) con espejo en
 * localStorage, así que barrer solo el espejo dejaría la sesión viva: al
 * siguiente arranque Supabase la restaura desde Preferences y el "cerré
 * sesión" no fue tal. Pasa cuando signOut() no alcanza a salir (sin señal en
 * el hospital, que es justo cuando más se presta un teléfono).
 *
 * No-op en web (ahí Preferences ni se usa) y a prueba de plugin ausente: si
 * algo falla, el logout sigue su curso.
 */
export async function clearNativeMipaeKeys() {
  try {
    if (!Capacitor.isNativePlatform()) return;
    const { keys } = await Preferences.keys();
    const objetivo = (keys || []).filter(k => LOCALSTORAGE_PREFIXES.some(p => k.startsWith(p)));
    await Promise.all(objetivo.map(k => Preferences.remove({ key: k }).catch(() => {})));
  } catch {
    // plugin ausente o fallo nativo: no bloquea el cierre de sesión
  }
}
